import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import i18next from "i18next";
import { getPermissions } from "../../api/apiRoles";

/**
 * Permissions response normalizer.
 *
 * The backend returns one of these shapes (always under `data`):
 *   1. { permissions: [...], groups: { module: [...] } }   ← current
 *   2. [...]                                              ← flat list (legacy)
 *   3. { data: [...] }                                    ← wrapped (legacy)
 *
 * In every case we expose:
 *   - permissions: flat array
 *   - grouped:     module -> permission[] (preserves backend's group key when present;
 *                  otherwise derived from `permission.group` or the prefix before the dot)
 */
export const useGetPermissions = () => {
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ["permissions", i18next.language],
        queryFn: getPermissions,
        staleTime: 5 * 60 * 1000,
    });

    const root = data?.data ?? data;

    const { permissions, grouped } = useMemo(() => {
        // Shape 1: { permissions, groups }
        if (root && Array.isArray(root.permissions) && root.groups && typeof root.groups === "object") {
            return { permissions: root.permissions, grouped: root.groups };
        }

        // Shape 4: Grouped object { module1: [...], module2: [...] } (New Shape)
        // Detect if root is an object where most values are arrays
        if (root && !Array.isArray(root) && typeof root === 'object') {
            const keys = Object.keys(root);
            const arraysCount = keys.filter(k => Array.isArray(root[k])).length;
            
            // If it's an object with multiple arrays and no 'permissions' key as a root list,
            // or if it clearly matches the new structure.
            if (arraysCount > 1 && !root.groups) {
                const flat = keys.flatMap(k => Array.isArray(root[k]) ? root[k] : []);
                return { permissions: flat, grouped: root };
            }
        }

        // Shape 2 / 3: flat array (with or without wrapper)
        const flat = Array.isArray(root)
            ? root
            : Array.isArray(root?.data)
                ? root.data
                : Array.isArray(root?.permissions)
                    ? root.permissions
                    : [];

        const grouped = flat.reduce((acc, p) => {
            const name = p?.name || "";
            const module = p?.group || (name.includes(".") ? name.split(".")[0] : "general");
            (acc[module] = acc[module] || []).push(p);
            return acc;
        }, {});

        return { permissions: flat, grouped };
    }, [root]);

    return { permissions, grouped, isLoading, error, refetch };
};
